// src/App.js
import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MCPProvider } from './components/ai/MCPProvider';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AIAssessmentProvider } from './components/ai/AIAssessmentProvider';
import { CharacterProvider } from './context/CharacterContext';
import './index.css';

import ApiService from './services/ApiService';
import DataSecurityGuidelinesModal from './components/auth/DataSecurityGuidelinesModal';

// Import components
import LandingPage from './pages/landingPage'; // Make sure the path is correct
import CyberAegisDashboard from './components/dashboard/CyberAegisDashboard';
import LearningJourneyDashboard from './components/dashboard/LearningJourneyDashboard';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChallengeHub from './components/challenges/ChallengeHub';
import CategoryChallenges from './components/challenges/CategoryChallenges';
import LearningModule from './components/learning/LearningModule';
import LeaderboardScreen from './components/leaderboard/LeaderboardScreen';
import CyberAegisForum from './components/forum/CyberAegisForum';
import ScenarioExperience from './components/scenario/ScenarioExperience';
import SecuritySimulationContainer from './components/SecuritySimulationContainer';
// import CyberSkillTree from './components/skillTree/CyberSkillTree';
import ChallengeDetail from './components/challenges/ChallengeDetail';
import ChallengeOverview from './components/challenges/ChallengeOverview';
import SimulationsList from './pages/SimulationsList';
import GamificationEngine from './components/gamificationengine/GamificationEngine';
import AchievementPage from './pages/Achievement'; // Import the Achievement page
import Profile from './pages/Profile';
import { AIAssessmentDashboard } from './components/ai/AIAssessmentProvider';
import MFASetup from './components/auth/MFASetup';
import DemoArena from './components/challenges/DemoArena';

// Create single ApiService instance
const apiService = new ApiService();
// const root = ReactDOM.createRoot(document.getElementById('root'));

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading, currentUser } = useAuth();

    if (isLoading) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Determine whether the user has accepted the data security & privacy guidelines.
    const hasAcceptedPolicies =
        currentUser?.hasAcceptedPolicies ??
        currentUser?.data?.hasAcceptedPolicies ??
        null;

    // If authenticated but has not accepted policies yet, show dashboard (blurred) with modal on top.
    // Treat missing/undefined/null as not accepted (e.g. old API or before /me returns it).
    if (isAuthenticated && hasAcceptedPolicies !== true) {
        return (
            <>
                {children}
                <DataSecurityGuidelinesModal />
            </>
        );
    }

    // Update ApiService token when user is authenticated
    if (currentUser) {
        const token = localStorage.getItem('authToken');
        if (token) {
            apiService.setAuthToken(token);
        }
    }

    return children;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return isAuthenticated ? <Navigate to="/" replace /> : children;
};

const AppRoutes = () => {
    const { currentUser, logout } = useAuth();

    const gamificationEngine = useMemo(() => {
        if (currentUser?.id) {
            return new GamificationEngine(currentUser.id, currentUser);
        }
        return null; // Return null if there's no user
    }, [currentUser]);

    return (
        <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />

            {/* The root path now shows the public landing page */}
            <Route path="/" element={<LandingPage />} />

            {/* The dashboard is now on a protected route */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <CyberAegisDashboard
                            apiService={apiService}
                            userProfile={currentUser}
                            onLogout={logout}
                        />
                    </ProtectedRoute>
                }
            />

            {/* Challenge Routes */}
            <Route path="/challenges" element={<ProtectedRoute><ChallengeHub apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />
            <Route path="/challenges/category/:category" element={<ProtectedRoute><CategoryChallenges apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />
            <Route path="/challenge-overview/:challengeSetId" element={<ProtectedRoute><ChallengeOverview apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />
            <Route path="/challenge/:id" element={<ProtectedRoute><ChallengeDetail apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />
            <Route
                path="/challenge-attempt/:challengeId"
                element={
                    <ProtectedRoute>
                        <SecuritySimulationContainer
                            apiService={apiService}
                            gamificationEngine={gamificationEngine}
                        />
                    </ProtectedRoute>}
            />
            <Route path="/simulations" element={<ProtectedRoute><SimulationsList apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />

            {/* Learning Routes */}
            <Route path="/learning" element={<ProtectedRoute><LearningJourneyDashboard apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />
            <Route path="/learning/module/:moduleId" element={<ProtectedRoute><LearningModule apiService={apiService} userProfile={currentUser} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />

            {/* Assessment Route */}
            <Route path="/achievements" element={<ProtectedRoute><AchievementPage /></ProtectedRoute>} />
            <Route path="/assessment" element={<ProtectedRoute><AIAssessmentDashboard /></ProtectedRoute>} />

            {/* Profile Routes */}
            <Route path="/profile" element={<ProtectedRoute><LandingPage /></ProtectedRoute>} />
            <Route path="/my-profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

            {/* Other Routes */}
            <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardScreen apiService={apiService} userProfile={currentUser} onLogout={logout} /></ProtectedRoute>} />
            <Route
                path="/community"
                element={
                    <ProtectedRoute>
                        <CyberAegisForum
                            apiService={apiService}
                            userProfile={currentUser}
                        />
                    </ProtectedRoute>
                }
            />
            <Route path="/simulation/:challengeId" element={<ProtectedRoute><SecuritySimulationContainer apiService={apiService} gamificationEngine={gamificationEngine} /></ProtectedRoute>} />
            <Route path="/scenario/:scenarioId" element={<ProtectedRoute><ScenarioExperience apiService={apiService} /></ProtectedRoute>} />
            <Route path="/simulation/:type" element={<SecuritySimulationContainer apiService={apiService} />} />
            {/* <Route path="/skill-tree" element={<ProtectedRoute><CyberSkillTree apiService={apiService} /></ProtectedRoute>} /> */}
            <Route path="/demo-arena" element={<ProtectedRoute><DemoArena apiService={apiService} /></ProtectedRoute>} />

            {/* MFA Setup Route */}
            <Route path="/mfa-setup" element={
                <ProtectedRoute>
                    <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] flex items-center justify-center p-4">
                        <MFASetup
                            onComplete={() => window.history.back()}
                            onCancel={() => window.history.back()}
                        />
                    </div>
                </ProtectedRoute>
            } />

            {/* Catch all route now safely redirects to the landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

const App = () => {
    return (
        <Router>
            <AuthProvider>
                <CharacterProvider>
                    <AIAssessmentProvider>
                        <MCPProvider>
                            <AppRoutes />
                        </MCPProvider>
                    </AIAssessmentProvider>
                </CharacterProvider>
            </AuthProvider>
        </Router>
    );
};

export default App;